import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { setEndDate, setStartDate } from "@store/date/dateSlice";
import type { AppDispatch } from "@store/store";

export interface UseMonthNavigationParams {
  startDate: string;
  endDate: string;
  momentLocale: string;
  dispatch: AppDispatch;
}

export const useMonthNavigation = ({ startDate, endDate, momentLocale, dispatch }: UseMonthNavigationParams) => {
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => new Date());

  const currentMonth = useMemo(
    () => `${moment(selectedMonth).format("MMMM").toUpperCase()} ${moment(selectedMonth).format("YYYY")}`,
    [selectedMonth, momentLocale]
  );

  const goToPrevMonth = useCallback(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().subtract(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const goToNextMonth = useCallback(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().add(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  return {
    showDateRangePicker,
    setShowDateRangePicker,
    selectedMonth,
    setSelectedMonth,
    currentMonth,
    goToPrevMonth,
    goToNextMonth,
  };
};
